import React from "react";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspMasterDataCard from "../../components/patterns/TspMasterDataCard";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import useTspLocationResourceData from "../../hooks/useTspLocationResourceData";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import TspBackButton from "../../components/common/TspBackButton";

const EMPTY_DRAFT = { state_id: "", city_name: "", facility_name: "", has_water: "", has_electricity: "", cooking_capacity: "", remarks: "" };
const YES_NO = ["Yes", "No"];

// has_water/has_electricity: the backend requires a strict JSON boolean (see
// CLAUDE.md Section 12 / the Phase 3 report) — "Yes"/"No" is only how the
// form presents that choice; it is converted here, never sent as a string.
const toBoolean = (value) => (value === "Yes" ? true : value === "No" ? false : undefined);
const fromBoolean = (value) => (value === true ? "Yes" : value === false ? "No" : "—");

export default function TspPrasadamPage() {
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.PRASADAM_MANAGE);
  const states = useTspApiResource(() => TspApiService.listStates(), []);
  const { loading, error, reload, records, add, remove } = useTspLocationResourceData(TspApiService.prasadam);

  if (loading) return <div className="tsp-page tsp-dashboard-loading">Loading prasadam facilities…</div>;
  if (error) return <TspLoadError error={error} onRetry={reload} what="prasadam facilities" />;

  const columns = [
    { key: "location", label: "City", render: (row) => row.location?.label || "—" },
    { key: "facility_name", label: "Facility Name" },
    { key: "has_water", label: "Water", render: (row) => fromBoolean(row.has_water) },
    { key: "has_electricity", label: "Electricity", render: (row) => fromBoolean(row.has_electricity) },
    { key: "cooking_capacity", label: "Cooking Capacity", render: (row) => (row.cooking_capacity == null ? "—" : `${row.cooking_capacity} persons`) },
    { key: "remarks", label: "Remarks", render: (row) => row.remarks || "—" },
  ];

  const fields = [
    { key: "state_id", label: "State", type: "select", options: (states.data || []).map((s) => ({ value: String(s.id), label: s.name })), required: true },
    { key: "city_name", label: "City", required: true },
    { key: "facility_name", label: "Facility Name", required: true },
    { key: "has_water", label: "Water", type: "select", options: YES_NO },
    { key: "has_electricity", label: "Electricity", type: "select", options: YES_NO },
    { key: "cooking_capacity", label: "Cooking Capacity" },
    { key: "remarks", label: "Remarks" },
  ];

  const handleAdd = (draft) => {
    const body = { state_id: Number(draft.state_id), city_name: draft.city_name.trim(), facility_name: draft.facility_name.trim() };
    const water = toBoolean(draft.has_water);
    const electricity = toBoolean(draft.has_electricity);
    if (water !== undefined) body.has_water = water;
    if (electricity !== undefined) body.has_electricity = electricity;
    if (draft.cooking_capacity !== "") body.cooking_capacity = Number(draft.cooking_capacity);
    if (draft.remarks.trim()) body.remarks = draft.remarks.trim();
    return add(body);
  };

  return (
    <div className="tsp-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Prasadam Facilities</h1>
          <TspBreadcrumb items={["Dashboard", "Prasadam"]} />
        </div>
        <TspBackButton fallback="/tsp" />
      </div>
      <TspMasterDataCard
        title="Prasadam Facilities"
        columns={columns}
        fields={fields}
        records={records}
        emptyDraft={EMPTY_DRAFT}
        canManage={canManage}
        onAdd={handleAdd}
        onDelete={remove}
        rowLabel={(row) => row.facility_name}
      />
    </div>
  );
}
